import { useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import type { MapCanvasHandle } from './MapCanvas'
import { cabinetApi } from '../api'
import { toContourGrid } from '../maps/isothermMath'
import { overlayGridKey } from './overlayLayers'
import { buildUpwIso, isoLevels } from './upwellingIso'
import { nearestMechDepth } from '../models/upwellingSelection'

/** Цвета семейств. Цветом различаются МЕХАНИЗМЫ (их до шести одновременно),
 *  а знак — штрихом: двенадцати различимых линий поверх растра не бывает.
 *
 *  Взяты из качественной палитры (различимы при дальтонизме) и намеренно не из
 *  бренд-пака: это не айдентика, а РАЗМЕТКА — партнёр, перекрасивший два
 *  семейства в близкие тона, сделал бы карту нечитаемой, не заметив этого. */
const FAMILY_COLORS: Record<string, string> = {
  upw_vertical: '#111827',
  upw_divergence: '#7f3f00',
  upw_ekman_coastal: '#0b6e4f',
  upw_eddy: '#7a0177',
  upw_topo: '#8c2d04',
  upw_plume: '#1c5cb0',
  upwelling: '#8c2d04',
}

export function upwFamilyColor(mechanism: string): string {
  return FAMILY_COLORS[mechanism] ?? '#111827'
}

/**
 * Изолинии выбранных механизмов поверх растра раздела.
 *
 * Сетка каждого механизма грузится ОДИН раз на вьюпорт (и заново по завершении
 * докачки — `loadNonce`), а уровни пересчитываются в браузере. Так переключение
 * «заливка ↔ линии» мгновенно, и один привезённый массив кормит и контуры, и
 * края шкалы легенды.
 *
 * Слои снимаются по механизму, а не оптом: снятый тумблер обязан dropLayers ровно
 * своё семейство, оставив соседние на месте.
 */
export function useUpwellingIso(
  mapRef: RefObject<MapCanvasHandle | null>,
  opts: {
    enabled: boolean
    mechanisms: string[]
    date: string
    depth: number
    viewKey: string
    loadNonce?: number
    zero: boolean
    /** Горизонты, на которых поле механизма за эту дату действительно лежит. */
    depthsOf?: (mechanism: string) => readonly number[] | null
  },
): { drawn: string[]; pending: boolean } {
  const { enabled, mechanisms, date, depth, viewKey, zero } = opts
  const loadNonce = opts.loadNonce ?? 0
  const [drawn, setDrawn] = useState<string[]>([])
  const [pending, setPending] = useState(false)
  // Что сейчас лежит на карте — чтобы снимать ровно снятое.
  const onMap = useRef<Set<string>>(new Set())

  const key = overlayGridKey({ date, depth, viewKey, loadNonce })
  const listKey = enabled ? mechanisms.join(',') : ''

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    let alive = true

    const dropLayers = (ids: Iterable<string>) => {
      for (const id of ids) map.removeUpwIso(id)
    }

    if (!enabled || mechanisms.length === 0) {
      dropLayers(onMap.current)
      onMap.current = new Set()
      setDrawn([])
      setPending(false)
      return
    }

    // Снимаем ушедшие ДО запроса: иначе выключенный тумблер оставлял бы линию
    // на экране на всё время сетевого ответа соседей.
    const wanted = new Set(mechanisms)
    dropLayers([...onMap.current].filter(id => !wanted.has(id)))
    onMap.current = new Set([...onMap.current].filter(id => wanted.has(id)))

    setPending(true)
    const bbox = map.getBBox()
    void (async () => {
      const laid: string[] = []
      for (const mech of mechanisms) {
        let resp = null
        try {
          resp = await cabinetApi.upwellingGrid({
            mechanism: mech, date,
            // Глубину шлём ВСЕГДА: безглубинный механизм её игнорирует на
            // сервере, а угадывать здесь, у кого она есть, значило бы завести
            // вторую копию каталога. А вот ГОРИЗОНТ у каждого свой: дивергенция
            // интегрируется к 30 и 50 м, и спрошенная на 10 м отдала бы пустую
            // сетку — тумблер включён, линий нет, причина не названа нигде.
            depth: nearestMechDepth(depth, opts.depthsOf?.(mech)),
            bbox,
          })
        } catch {
          resp = null
        }
        if (!alive) return
        const grid = toContourGrid(resp)
        if (!grid || resp?.vmin == null || resp?.vmax == null) {
          map.removeUpwIso(mech)
          onMap.current.delete(mech)
          continue
        }
        // Знаковость выводим из самого поля: отрицательные значения бывают
        // только у знаковых механизмов, и второй копии реестра здесь не нужно.
        const signed = resp.vmin < 0
        const levels = isoLevels(resp.vmin, resp.vmax, { signed, zero: zero && signed })
        if (levels.length === 0) {
          map.removeUpwIso(mech)
          onMap.current.delete(mech)
          continue
        }
        const family = buildUpwIso(grid, levels, upwFamilyColor(mech), mech)
        map.setUpwIso(mech, family.contours, family.labels)
        onMap.current.add(mech)
        laid.push(mech)
      }
      if (!alive) return
      setDrawn(laid)
      setPending(false)
    })()

    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, listKey, key, zero])

  // Уборка при уходе с раздела: слои живут на карте, а не в этом компоненте.
  useEffect(() => () => {
    const map = mapRef.current
    if (!map) return
    for (const id of onMap.current) map.removeUpwIso(id)
    onMap.current = new Set()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { drawn, pending }
}
